import type { UserInfo } from "@/pages/login/type/loginType";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  AUTH_COOKIE_KEY as COOKIE_KEY,
  AUTH_STORAGE_KEY as STORAGE_KEY,
  deleteCookie,
  getCookie,
  setCookie,
} from "@/common/utils/authStorage";

interface AccountState {
  access_token: string | null;
  refresh_token: string | null;
  user_info: UserInfo | null;
  token_type: string | null;
}

interface AccountActions {
  setAuth: (
    access_token: string,
    refresh_token: string,
    token_type: string,
    keepLogin: boolean,
  ) => void;
  updateTokens: (
    access_token: string,
    refresh_token: string,
    token_type: string,
  ) => void;
  setUserInfo: (user_info: UserInfo) => void;
  reset: () => void;
}

const initialState: AccountState = {
  access_token: null,
  refresh_token: null,
  user_info: null,
  token_type: null,
};

// session cookie에서 토큰 가져오기
const getAuthFromCookie = (): AccountState => {
  const cookieData = getCookie(COOKIE_KEY);
  if (cookieData) {
    try {
      return JSON.parse(cookieData);
    } catch {
      return initialState;
    }
  }
  return initialState;
};

// zustand persist store (localStorage용)
const usePersistedStore = create<AccountState>()(
  persist(() => initialState, {
    name: STORAGE_KEY,
    partialize: (state) => ({
      access_token: state.access_token,
      refresh_token: state.refresh_token,
      user_info: state.user_info,
      token_type: state.token_type,
    }),
  }),
);

/**
 * 소속 구조 개편 전에 저장된 user_info(company 형태)를 버린다.
 * 남겨두면 affiliation 이 undefined 인 채로 권한 분기가 돌아간다.
 * 버려도 useSyncMyInfo 가 authoritative 값을 다시 채운다.
 */
const withValidUserInfo = (state: AccountState): AccountState =>
  state.user_info && !state.user_info.affiliation
    ? { ...state, user_info: null }
    : state;

// 초기 상태: localStorage(zustand) 먼저, 없으면 cookie
const getInitialAuth = (): AccountState => {
  const persistedState = usePersistedStore.getState();
  if (persistedState.access_token) {
    return withValidUserInfo(persistedState);
  }
  return withValidUserInfo(getAuthFromCookie());
};

export const useAccountStore = create<AccountState & AccountActions>()(
  (set) => ({
    ...getInitialAuth(),
    setAuth: (
      access_token: string,
      refresh_token: string,
      token_type: string,
      keepLogin: boolean,
    ) => {
      if (keepLogin) {
        usePersistedStore.setState({ access_token, refresh_token, token_type });
        deleteCookie(COOKIE_KEY);
      } else {
        setCookie(
          COOKIE_KEY,
          JSON.stringify({ access_token, refresh_token, token_type }),
        );
        usePersistedStore.setState(initialState);
        localStorage.removeItem(STORAGE_KEY);
      }

      set(() => ({ access_token, refresh_token, token_type }));
    },
    updateTokens: (
      access_token: string,
      refresh_token: string,
      token_type: string,
    ) => {
      const persistedState = usePersistedStore.getState();

      // localStorage에 저장된 경우 (keepLogin이 true였던 경우)
      if (persistedState.access_token) {
        usePersistedStore.setState({
          access_token,
          refresh_token,
          token_type,
        });
      } else {
        // session cookie에 저장된 경우 (기존 데이터 유지하며 토큰만 갱신)
        const cookieData = getCookie(COOKIE_KEY);
        let existing = {};
        if (cookieData) {
          try {
            existing = JSON.parse(cookieData);
          } catch {
            // cookie 파싱 실패 시 무시
          }
        }
        setCookie(
          COOKIE_KEY,
          JSON.stringify({ ...existing, access_token, refresh_token, token_type }),
        );
      }

      set(() => ({ access_token, refresh_token, token_type }));
    },
    setUserInfo: (user_info) => {
      const persistedState = usePersistedStore.getState();

      if (persistedState.access_token) {
        // localStorage에 저장된 경우 (keepLogin이 true였던 경우)
        usePersistedStore.setState({ user_info });
      } else {
        // session cookie에 저장된 경우
        const cookieData = getCookie(COOKIE_KEY);
        if (cookieData) {
          try {
            const parsed = JSON.parse(cookieData);
            setCookie(COOKIE_KEY, JSON.stringify({ ...parsed, user_info }));
          } catch {
            // cookie 파싱 실패 시 무시
          }
        }
      }

      set(() => ({
        user_info,
      }));
    },
    reset: () => {
      usePersistedStore.setState(initialState);
      localStorage.removeItem(STORAGE_KEY);
      deleteCookie(COOKIE_KEY);
      set(initialState);
    },
  }),
);
